/**
 * Řazení, filtrování, štítky a skóre doporučení nad množinou itinerářů.
 */
import type { Itinerary, SortKey, SortOptions } from './types';

export const SORT_LABELS: Record<SortKey, string> = {
  score: 'Doporučené',
  time: 'Nejrychlejší',
  arrival: 'Nejdřívější příjezd',
  departure: 'Nejpozdější odjezd',
  cost: 'Nejlevnější',
  wait: 'Nejméně čekání',
  emissions: 'Nejekologičtější',
  distance: 'Nejkratší',
  comfort: 'Nejpohodlnější',
  transfers: 'Nejméně přestupů',
};

export function defaultSortOptions(): SortOptions {
  return {
    key: 'score',
    carFreeOnly: false,
    accessibleOnly: false,
    weights: { time: 1, cost: 0.5, comfort: 0.3, emissions: 0.3 },
  };
}

/** Normalizované skóre 0–1 (nižší = lepší) podle vah uživatele. */
export function score(it: Itinerary, all: Itinerary[], w: SortOptions['weights']): number {
  const norm = (v: number, min: number, max: number): number =>
    max - min < 1e-9 ? 0 : (v - min) / (max - min);
  const ts = all.map((x) => x.totalMin);
  const cs = all.map((x) => x.cost);
  const es = all.map((x) => x.emissions);
  const t = norm(it.totalMin, Math.min(...ts), Math.max(...ts));
  const c = norm(it.cost, Math.min(...cs), Math.max(...cs));
  const e = norm(it.emissions, Math.min(...es), Math.max(...es));
  const comfortPenalty = (5 - it.comfort) / 4; // 0 = max komfort
  const wSum = w.time + w.cost + w.comfort + w.emissions || 1;
  return (w.time * t + w.cost * c + w.comfort * comfortPenalty + w.emissions * e) / wSum;
}

/** Seřadí a vyfiltruje itineráře podle voleb. Vrací novou kopii. */
export function sortItineraries(items: Itinerary[], opts: SortOptions): Itinerary[] {
  let out = [...items];
  if (opts.carFreeOnly) out = out.filter((i) => i.carFree);
  if (opts.accessibleOnly) out = out.filter((i) => i.accessible);

  const cmpNum = (sel: (i: Itinerary) => number) => (a: Itinerary, b: Itinerary) =>
    sel(a) - sel(b) || a.totalMin - b.totalMin;

  switch (opts.key) {
    case 'time':
      out.sort(cmpNum((i) => i.totalMin));
      break;
    case 'arrival':
      out.sort(cmpNum((i) => i.arrivalMs));
      break;
    case 'departure':
      // Nejpozdější odjezd nahoře.
      out.sort((a, b) => b.departureMs - a.departureMs || a.totalMin - b.totalMin);
      break;
    case 'cost':
      out.sort(cmpNum((i) => i.cost));
      break;
    case 'wait':
      out.sort(cmpNum((i) => i.waitMin));
      break;
    case 'emissions':
      out.sort(cmpNum((i) => i.emissions));
      break;
    case 'distance':
      out.sort(cmpNum((i) => i.distanceKm));
      break;
    case 'comfort':
      out.sort((a, b) => b.comfort - a.comfort || a.totalMin - b.totalMin);
      break;
    case 'transfers':
      out.sort(cmpNum((i) => i.transfers));
      break;
    case 'score':
      out.sort(cmpNum((i) => score(i, items, opts.weights)));
      break;
  }
  return out;
}

/**
 * Přidá štítky („Nejrychlejší", „Nejlevnější"…) napříč množinou.
 * Mutuje pole badges jednotlivých itinerářů (po vyčištění).
 */
export function assignBadges(items: Itinerary[], weights: SortOptions['weights']): void {
  for (const it of items) it.badges = [];
  if (items.length === 0) return;

  const tag = (sel: (i: Itinerary) => number, label: string, lowerBetter = true): void => {
    let best = items[0];
    for (const it of items) {
      if (lowerBetter ? sel(it) < sel(best) : sel(it) > sel(best)) best = it;
    }
    // Štítek dává smysl, jen když varianta opravdu vede (ne při shodě všech).
    const values = items.map(sel);
    const allSame = Math.max(...values) - Math.min(...values) < 1e-9;
    if (!allSame) best.badges.push(label);
  };

  tag((i) => i.totalMin, 'Nejrychlejší');
  tag((i) => i.cost, 'Nejlevnější');
  tag((i) => i.emissions, 'Nejekologičtější');
  tag((i) => i.comfort, 'Nejpohodlnější', false);
  if (items.some((i) => i.waitMin > 0)) tag((i) => i.waitMin, 'Nejméně čekání');

  // Doporučení podle skóre — s vysvětlením, z čeho vychází.
  for (const it of items) it.recommendReason = undefined;
  if (items.length > 1) {
    let best = items[0];
    for (const it of items) {
      if (score(it, items, weights) < score(best, items, weights)) best = it;
    }
    best.badges.unshift('Doporučujeme');
    best.recommendReason = recommendReason(best, items, weights);
  }

  for (const it of items) {
    if (it.transfers === 0 && it.segments.some((s) => s.kind === 'transit')) {
      it.badges.push('Bez přestupů');
    }
  }
}

/**
 * Lidské vysvětlení doporučení: nejlepší vážený kompromis + konkrétní
 * silné stránky (metriky, ve kterých varianta vede nebo je těsně za vedoucí).
 */
export function recommendReason(
  it: Itinerary,
  all: Itinerary[],
  w: SortOptions['weights'],
): string {
  const strengths: string[] = [];
  const nearBest = (sel: (i: Itinerary) => number, tolerance: number): boolean => {
    const best = Math.min(...all.map(sel));
    return sel(it) <= best + tolerance * Math.max(1e-9, best);
  };
  if (nearBest((i) => i.totalMin, 0.05)) strengths.push('čas');
  if (nearBest((i) => i.cost, 0.05)) strengths.push('cena');
  if (it.comfort >= Math.max(...all.map((i) => i.comfort)) - 0.25) strengths.push('komfort');
  if (nearBest((i) => i.emissions, 0.05)) strengths.push('emise');
  if (all.some((i) => i.waitMin > 0.4) && nearBest((i) => i.waitMin, 0.05)) strengths.push('málo čekání');

  const pct = (x: number): number => Math.round(x * 100);
  const weightsTxt = `čas ${pct(w.time)} %, cena ${pct(w.cost)} %, komfort ${pct(w.comfort)} %, emise ${pct(w.emissions)} %`;
  const base = `Nejlepší vážený kompromis podle vašich vah (${weightsTxt}).`;
  return strengths.length > 0 ? `${base} Vyniká v: ${strengths.join(', ')}.` : base;
}

/** Souhrnná čísla pro hlavičku výsledků. */
export function summarize(items: Itinerary[]): {
  fastestMin: number;
  cheapest: number;
  greenest: number;
} | null {
  if (items.length === 0) return null;
  return {
    fastestMin: Math.min(...items.map((i) => i.totalMin)),
    cheapest: Math.min(...items.map((i) => i.cost)),
    greenest: Math.min(...items.map((i) => i.emissions)),
  };
}
